import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { formatAddress, formatCents, formatPhone, type Customer } from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ListFoot, usePagedList } from '../components/PagedList';
import { PageFooter } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SearchField,
  SortTh,
  Spinner,
  Table,
  Td,
  Th,
  sortRows,
  useTableSort
} from '../components/ui';
import { NewCustomerModal } from '../components/NewCustomerModal';
import {
  ConfirmDeleteModal,
  SelectCell,
  SelectionBar,
  useSelection,
} from '../components/Selection';

export function CustomersPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const { can } = useAuth();

  const list = usePagedList<Customer>({
    queryKey: keys.customers(q),
    fetchPage: (cursor, limit) =>
      api.customers.list({ q: q.trim() || undefined, cursor, limit }),
  });
  const customers = list.items;
  const isLoading = list.isLoading;

  /*
   * Sorted here rather than by the server, deliberately: this is the page the
   * search already narrowed, and re-querying on every heading click would put
   * a network round trip behind a gesture that should be instant.
   *
   * Note that this sorts what is *loaded*. With a long list only partly
   * fetched, clicking a heading orders those rows and the rest arrive after —
   * which is honest as long as the foot says how many are still to come, and
   * it does. The alternative, sorting on the server, puts a round trip behind
   * every click for the sake of a case the foot already explains.
   */
  const sort = useTableSort<
    'name' | 'phone' | 'address' | 'source' | 'type' | 'jobs' | 'outstanding'
  >('name');
  const sorted = sortRows(customers, sort, (customer, key) => {
    const primary = customer.properties?.find((p) => p.isPrimary) ?? customer.properties?.[0];
    switch (key) {
      case 'name':
        return customer.name;
      case 'phone':
        return customer.phone;
      case 'address':
        return primary ? formatAddress(primary) : null;
      case 'source':
        return customer.latestJob?.leadSourceName ?? null;
      case 'type':
        return customer.latestJob?.jobTypeName ?? null;
      case 'jobs':
        return customer.jobCount ?? 0;
      case 'outstanding':
        return customer.outstandingCents ?? 0;
    }
  });

  /* Ticking rows and deleting them — see components/Selection.tsx. */
  const selection = useSelection(sorted);
  const [confirming, setConfirming] = useState(false);
  const qc = useQueryClient();

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Customers"
        subtitle={`${list.total.toLocaleString()} on file`}
        actions={
          <>
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Dana, 555-0142, Buckthorn…"
              label="Search customers"
              className="w-[300px]"
            />
            {/*
              Import sits beside New, not behind a menu. Somebody arriving
              with a spreadsheet is on this screen looking at an empty list,
              and that is the moment the button has to be visible — a
              migration path nobody finds is not a migration path.

              Admin only, like the route behind it: importing rewrites the
              whole customer list, which is not a dispatcher's decision.
            */}
            {can('admin') && (
              <Button icon="file" onClick={() => navigate('/import?type=customers')}>
                Import
              </Button>
            )}
            {can('admin', 'dispatcher') && (
              <Button variant="primary" icon="plus" onClick={() => setCreating(true)}>
                New customer
              </Button>
            )}
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto max-w-[1860px]">
          <Card
            title="All customers"
            icon="users"
            count={customers.length}
            divided
            bodyClassName="p-0"
          >
            {isLoading ? (
              <div className="grid place-items-center py-16">
                <Spinner className="size-5 text-ink-4" />
              </div>
            ) : customers.length === 0 ? (
              <EmptyState
                title={q ? `Nothing matches “${q}”` : 'No customers yet'}
                description={
                  q
                    ? 'Search covers names, phone numbers and street addresses.'
                    : 'Add your first customer to start booking work.'
                }
              />
            ) : (
              <>
                <SelectionBar
                  count={selection.count}
                  one="customer"
                  many="customers"
                  onClear={selection.clear}
                  onDelete={() => setConfirming(true)}
                />
                <Table>
                <thead>
                  <tr>
                    {can('admin') && (
                      <Th className="w-[44px]">
                        <SelectCell
                          checked={selection.allVisible}
                          indeterminate={selection.someVisible}
                          onChange={selection.toggleAll}
                          label="Select all customers"
                        />
                      </Th>
                    )}
                    <SortTh sortKey="name" sort={sort} className="w-[26%]">Customer</SortTh>
                    <SortTh sortKey="phone" sort={sort}>Phone</SortTh>
                    <SortTh sortKey="address" sort={sort}>Address</SortTh>
                    <SortTh sortKey="source" sort={sort}>Lead source</SortTh>
                    <SortTh sortKey="type" sort={sort}>Job type</SortTh>
                    <SortTh sortKey="jobs" sort={sort}>Jobs</SortTh>
                    <SortTh sortKey="outstanding" sort={sort} align="right">Outstanding</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((customer: Customer) => {
                    const primary =
                      customer.properties?.find((p) => p.isPrimary) ?? customer.properties?.[0];
                    return (
                      <tr
                        key={customer.id}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        className="cursor-pointer transition-colors hover:bg-raised"
                      >
                        {can('admin') && (
                          <Td>
                            <SelectCell
                              checked={selection.isSelected(customer.id)}
                              onChange={() => selection.toggle(customer.id)}
                              label={`Select ${customer.id}`}
                            />
                          </Td>
                        )}
                        <Td>
                          <div className="text-[14px] font-semibold text-ink">{customer.name}</div>
                          {customer.email && (
                            <div className="mt-1 text-[12px] text-ink-4">{customer.email}</div>
                          )}
                        </Td>
                        <Td className="tnum">{formatPhone(customer.phone)}</Td>
                        <Td>
                          {primary ? formatAddress(primary) : '—'}
                          {(customer.properties?.length ?? 0) > 1 && (
                            <Badge tone="slate" className="ml-2">
                              +{(customer.properties?.length ?? 1) - 1}
                            </Badge>
                          )}
                        </Td>
                        {/*
                         * Both come off the customer's most recent job, because
                         * that is where they are recorded — a customer found
                         * through a yard sign in March and a Google search in
                         * September is two lead sources, and the latest one is
                         * the one worth seeing next to their name.
                         */}
                        <Td>
                          {customer.latestJob?.leadSourceName ?? (
                            <span className="text-ink-4">—</span>
                          )}
                        </Td>
                        <Td>
                          {customer.latestJob?.jobTypeName ?? <span className="text-ink-4">—</span>}
                        </Td>
                        <Td className="tnum">{customer.jobCount ?? 0}</Td>
                        <Td align="right" className="tnum">
                          {customer.outstandingCents ? (
                            <span className="font-semibold text-danger">
                              {formatCents(customer.outstandingCents)}
                            </span>
                          ) : (
                            <span className="text-ink-4">—</span>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
                </Table>
                <ListFoot list={list} one="customer" many="customers" />
            </>
            )}
          </Card>
        </div>

        <PageFooter label={`${customers.length} customers`} />
      </div>

      <NewCustomerModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(customer) => navigate(`/customers/${customer.id}`)}
      />

      <ConfirmDeleteModal
        open={confirming}
        onClose={() => setConfirming(false)}
        count={selection.count}
        one="customer"
        many="customers"
        warning="Their addresses, notes and recurring schedules go too. A customer with jobs or invoices cannot be deleted — those are named back to you if any are in the way."
        onConfirm={() => api.customers.bulkRemove(selection.selected)}
        onDeleted={() => {
          void qc.invalidateQueries({ queryKey: ['customers'] });
          selection.clear();
        }}
      />

    </>
  );
}
